import React, { useState } from 'react';
import { signInWithPopup, GoogleAuthProvider, signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from 'react-router-dom';
import '../styles/Academy.css';
import logobot from '../assets/logobot.png';
import { auth } from '../firebase';

const googleProvider = new GoogleAuthProvider();

const Academy = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (error) {
      let errorMessage = 'An error occurred during sign in';
      
      switch (error.code) {
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email. Please sign up first.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password. Please try again.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        default:
          errorMessage = error.message.replace('Firebase: ', '');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    
    try {
      await signInWithPopup(auth, googleProvider);
      navigate('/dashboard');
    } catch (error) {
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestAccess = () => {
    // Store guest status in session storage
    sessionStorage.setItem('guestUser', 'true');
    navigate('/academy/courses');
  };

  return (
    <div className="login-page">
      <div className="login-container">

        <div className="login-header">
          <div className="logo-section">
            <div className="logo-wrapper">
              <img src={logobot} alt="The Firm" className="logo-image" />
            </div>
            <div className="badge-wrapper">
              <span className="badge">Academy</span>
            </div>
          </div>
        </div>

        <div className="login-content">
          <div className="welcome-text">
            <h1>Welcome to</h1>
            <h1 className="highlight">The Future</h1>
            <h1>of Trading</h1>
          </div>

          <div className="login-description">
            <p>Sign in to access exclusive educational</p>
            <p>content and AI tools</p>
          </div>

          <form className="login-form" onSubmit={handleEmailSignIn}>
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-fields">
              <div className="input-group">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
              <div className="input-group">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className="form-links">
              <a href="/signup" className="link">Need an account? Sign Up</a>
              <a href="/reset-password" className="link">Forgot Password?</a>
            </div>

            <div className="button-group">
              <button 
                type="submit" 
                className="sign-in"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <button 
                type="button"
                onClick={handleGuestAccess}
                className="guest-access"
              >
                Continue as Guest
              </button>
            </div>

            <div className="or-divider">
              <span>OR</span>
            </div>

            <button 
              type="button"
              className="google-sign-in"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {loading ? 'Connecting...' : 'Continue with Google'}
            </button>
          </form>
        </div>

        <div className="login-footer">
          <div className="secure-login">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M16.67 9.17v-2.5c0-3.68-2.98-6.67-6.67-6.67S3.33 2.99 3.33 6.67v2.5c-.92 0-1.66.75-1.66 1.66v6.67c0 .92.74 1.67 1.66 1.67h13.34c.92 0 1.66-.75 1.66-1.67v-6.67c0-.91-.74-1.66-1.66-1.66zm-11.67-2.5c0-2.76 2.24-5 5-5s5 2.24 5 5v2.5H5v-2.5z" fill="#777E90"/>
            </svg>
            Secure Login
          </div>
          <div className="powered-by">
            Powered by FirmAI
          </div>
        </div>
      </div>
    </div>
  );
};

export default Academy;